import { Opt } from 'app/tools/general.types';
import { SafeMap } from 'app/tools/utils';
import { Except } from 'type-fest';
import {
  Product,
  ProductFromSearchV1,
} from '../product/api-product.types';

export type AssignProductRtn = {
  id: number;
  detail: string;
};

export type AssignedProduct = {
  id: number;
  productVariant: string;
  productDetails: Product;
  purchaseDate: string;
  repairDetails: RepairDetails;
  purchasePlace?: string;
  purchasePrice?: CurrencyObject;
  purchaseCity?: string;
  purchaseCountry?: string;
  serialNumber?: string;
  registered?: string;
  warrantyDetails?: WarrantyDetails;
  expiryTerm?: number;
  receiptImage?: string;
  userProductImage?: string;
};

type RepairDetails = {
  repairerName: string;
};

export type ProductLite = {
  id: string;
  name: string;
  imageUrl?: string;
  source?: string;
  userManual?: string;
};
export type AssignedProductLite = {
  id: number;
  productDetails: ProductLite;
  purchaseDate: string;
  registered?: string;
  warrantyDetails?: WarrantyDetails;
  expiryTerm?: number;
  userProductImage?: string;
};

export type RepairAssignedProduct = {
  id: number;
  currentStatus: string;
  customerName: string;
  date: string;
  issueType: string;
  time: string;
  productName: string;
  imageUrl?: string;
};

type CurrentSections = 'coverage' | 'rights';
export type WarrantyDetails<T extends string = CurrentSections> = {
  id: number;
  providerLegalName: string;
  url?: string;
  expiryTerm?: number;
} & {
  [x in `${T}Section`]?: WarrantySection;
};

export type WarrantySection = {
  id: number;
  completed: boolean;
  selection?: SelectionEntity[];
};

export enum SelectionRating {
  NO = 'no',
  OTHER = 'other',
  YES = 'yes',
}
export type SelectionEntity = {
  id: number;
  displayValue: string;
  internalValue: string;
  rating: SelectionRating | string;
  synthesis?: string;
};

type CurrencyObject = {
  amount?: string;
  currency?: string;
};

export const currencyCodes = {
  GBP: '£',
  EUR: '€',
  USD: 'US$',
  AUD: 'A$',
  JPY: '¥',
  CAD: 'C$',
  CHF: 'CHF',
  CNY: '¥',
  HKD: 'HK$',
  NZD: 'NZ$',
  SEK: 'kr',
  KRW: '₩',
  SGD: 'S$',
  NOK: 'kr',
  MUR: 'Rs',
  MXN: '$',
  INR: '₹',
  RUB: '₽',
  ZAR: 'R',
  TRY: '₺',
  BRL: 'R$',
};

export const mapCurrencyCodeToSymbol = new SafeMap(currencyCodes);

export const getExpiryTerm = (
  product?: AssignedProductLite
): Opt<number> =>
  product &&
  (product.expiryTerm ?? product.warrantyDetails?.expiryTerm);

export const getPurchasePriceFormatted = ({
  purchasePrice: price,
}: AssignedProduct): Opt<string> => {
  const currencySymbol = mapCurrencyCodeToSymbol.get(price?.currency);
  return price && price.amount && currencySymbol
    ? `${currencySymbol}${price.amount}`
    : undefined;
};

export type AssignProductReq = {
  productVariant: string;
  purchaseDate: string;
  purchasePlace?: string;
  expiryTerm?: number | null;
  purchasePrice: Opt<CurrencyObject>;
  purchaseCity?: string;
  purchaseCountry: string;
  serialNumber?: string;
  isFromSale?: boolean;
};
export type AssignV1ProductReq = Except<
  AssignProductReq,
  'productVariant'
> & { upcProductId: string };

export type AssignProductReqBlank = {
  upcProductId: string;
};

export type AssignProductReceiptImgReq = {
  receiptImage?: File;
};
export type AssignProductCustomImgReq = {
  userProductImage?: File;
};
export type AssignProductImageRtn = {
  status?: string;
};

export const isAsgnProduct = (
  input: AssignedProductLite | ProductLite | ProductFromSearchV1
): input is AssignedProductLite => 'productDetails' in input;

export const isSearchResult = (
  input: AssignedProductLite | ProductLite | ProductFromSearchV1
): input is ProductFromSearchV1 => 'isNewDatabaseProduct' in input;

export const isFullAssignment = (
  input: Opt<AssignedProductLite | AssignedProduct>
): input is AssignedProduct => 'productVariant' in (input || {});

export const productImg = (
  input?: ProductLite | AssignedProductLite | ProductFromSearchV1
): Opt<string> => {
  if (!input) return undefined;
  const product = isAsgnProduct(input) ? input.productDetails : input;
  return (
    (isAsgnProduct(input) && input.userProductImage) ||
    product.imageUrl
  );
};
